import type { CallDirection, CallOutcome } from '@crm/contracts';
import type { BadgeProps } from '@/components/ui/badge';

/**
 * How a call reads on screen, decided once.
 *
 * The labels themselves come from the contract (`CALL_OUTCOME_LABELS`,
 * `CALL_DIRECTION_LABELS`) so a new outcome appears everywhere by being added
 * in one place. What lives here is the presentation the contract has no
 * opinion about: which tone each outcome carries and how a duration reads.
 */

type Tone = NonNullable<BadgeProps['tone']>;

/**
 * Connected is the only good outcome, so it is the only green one.
 *
 * Missed is red rather than amber on purpose: an inbound call nobody answered
 * is the thing §11 exists to stop falling through the cracks, and an amber
 * chip in a list of amber chips is not a signal.
 */
export const OUTCOME_TONE: Record<CallOutcome, Tone> = {
  in_progress: 'info',
  connected: 'ok',
  missed: 'danger',
  no_answer: 'warn',
  busy: 'warn',
  wrong_number: 'neutral',
  voicemail: 'info',
};

export const DIRECTION_TONE: Record<CallDirection, Tone> = {
  inbound: 'info',
  outbound: 'neutral',
};

/**
 * How long, in words. Mirrors `formatDuration` in the backend's
 * `calls.service.ts` so the timeline summary the server wrote and the badge
 * the browser draws never disagree about what 90 seconds is called.
 */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * "2m 14s" into 134. The log form lets an operator type either a plain number
 * of seconds or the "mm:ss" their handset shows them, because asking someone
 * to convert 4:07 into 247 in their head is how durations get left at zero.
 */
export function parseDuration(input: string): number | null {
  const text = input.trim();
  if (!text) return 0;

  if (/^\d{1,3}(:[0-5]?\d){1,2}$/.test(text)) {
    return text
      .split(':')
      .map(Number)
      .reduce((total, part) => total * 60 + part, 0);
  }

  // "3m 20s" / "3m" / "20s"
  const units = text.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/i);
  if (units && (units[1] || units[2] || units[3])) {
    return Number(units[1] ?? 0) * 3600 + Number(units[2] ?? 0) * 60 + Number(units[3] ?? 0);
  }

  if (/^\d+$/.test(text)) return Number(text);
  return null;
}
